import axios from "axios";
import { getLoggedinUser } from "../../api/apiCore";
import logger from "../../utils/logger";

// Helper function to get company ID from logged in user
const getCompanyId = (): string | null => {
  const user = getLoggedinUser();
  return user?.companyID || null;
};

// Helper function to get base URL
const getBaseUrl = (): string => {
  return (
    process.env.REACT_APP_PROCESS_API_BASE_URL ||
    "https://dev-agent-proxy.jesss.ai"
  );
};

// Helper function to check if URL is an agent-proxy URL
export const isAgentProxyUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.pathname.includes("agent-proxy") ||
      parsedUrl.hostname.includes("agent-proxy")
    );
  } catch {
    return false;
  }
};

// Download file response interface
interface DownloadFileResponse {
  data: Blob;
  fileName: string;
  contentType: string;
}

// Download file through agent-proxy API
export const downloadFileFromAgentProxy = async (
  downloadURL: string,
): Promise<DownloadFileResponse> => {
  // Parse the URL to extract query parameters
  const parsedURL = new URL(downloadURL);
  const queryParams = parsedURL.searchParams;

  const fileURL = queryParams.get("url");
  const timestamp = queryParams.get("t");
  const token = queryParams.get("token");

  if (!fileURL && !token) {
    throw new Error("Missing required query parameters: url, t, or token");
  }

  // Reconstruct the URL with the same parameters
  const endpoint = `${getBaseUrl()}/api/workflow/download`;
  const params = new URLSearchParams();
  fileURL && params.append("url", fileURL);
  timestamp && params.append("t", timestamp);
  token && params.append("token", token);

  const fullURL = `${endpoint}?${params.toString()}`;

  const apiKey = process.env.REACT_APP_PROCESS_API_KEY;
  const companyId = getCompanyId();

  // We need to use a fresh axios instance to bypass the global interceptor
  // that returns response.data, since we need access to headers
  const axiosInstance = axios.create();

  const response = await axiosInstance.get(fullURL, {
    responseType: "blob",
    withCredentials: true,
    headers: {
      accept: "application/octet-stream",
      ...(companyId && { "X-Company-ID": companyId }),
      ...(apiKey && { Authorization: apiKey }),
    },
  });

  if (response.status !== 200) {
    throw new Error(`Failed to download file: ${response.status}`);
  }

  // Extract filename from Content-Disposition header if available
  const contentDisposition = response.headers["content-disposition"];
  let fileName = "download";

  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (fileNameMatch && fileNameMatch[1]) {
      fileName = fileNameMatch[1].replace(/['"]/g, "");
    }
  } else if (fileURL) {
    // Try to extract filename from the original URL
    try {
      const originalUrl = new URL(fileURL);
      const pathParts = originalUrl.pathname.split("/");
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart) {
        fileName = decodeURIComponent(lastPart);
      }
    } catch {
      // Fallback to generic filename
    }
  }

  const contentType =
    response.headers["content-type"] || "application/octet-stream";

  return {
    data: response.data,
    fileName,
    contentType,
  };
};

// Download file and trigger browser download
export const downloadFile = async (
  url: string,
  fileName?: string,
): Promise<void> => {
  try {
    let blob: Blob;
    let downloadFileName: string;

    if (isAgentProxyUrl(url)) {
      // Use special agent-proxy download
      const result = await downloadFileFromAgentProxy(url);
      blob = result.data;
      downloadFileName = fileName || result.fileName;
    } else {
      // Regular download - use fresh axios instance to bypass global interceptor
      const axiosInstance = axios.create();
      const response = await axiosInstance.get(url, { responseType: "blob" });
      blob = response.data;
      downloadFileName = fileName || url.split("/").pop() || "download";
    }

    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    logger.error("Error downloading file:", error);
    throw error;
  }
};
